import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { H3 } from 'shared-assets/components/Elements';
import { Col, Row } from 'react-styled-flexboxgrid';
import Card from 'shared-assets/components/Card';
import { Tooltip } from 'react-tippy';
import { DetailsTable } from 'shared-assets/components/Table';
import colors from 'shared-assets/lib/colors';
import {
  AttentionFilledIcon,
  Box,
  PlusOutlinedIcon,
  ThemeProvider,
  Typography,
} from '@kareo/design-system';
import { Skeleton } from '@material-ui/lab';
import Banner from 'shared-assets/components/Banner';
import AccessLink from 'shared-assets/facesheet-ui/components/ProtectedPatientChart/ChartAccess/AccessLink';
import getDesignSystemTheme from 'shared-assets/lib/utils/getDesignSystemTheme';
import rollbar from 'shared-assets/src/errors/errors';
import featureToggles from 'shared-assets/lib/sdk/featureToggles';
import {
  COMING_SOON_MESSAGE,
  PREFERRED_NAME_PRONOUNS_FT,
} from 'shared-assets/facesheet-ui/constants';
import { getPatientById } from 'shared-assets/facesheet-ui/service/patient/service';
import patientData from 'shared-assets/lib/patient';
import {
  formatISODateShort,
  formatPatientName,
} from 'shared-assets/facesheet-ui/utils/patientHelper';

const PatientInfoWidget = props => {
  const dsTheme = getDesignSystemTheme();
  const patientPhotoUrl = `/notes-ui/api/Patient/${props.patientId}/Photo/Img128`;
  const [isPreferredNameFTEnabled, setIsPreferredNameFTEnabled] = useState(
    false,
  );
  const [patient, setPatient] = useState(undefined);
  const [balance, setBalance] = useState('Unavailable');
  const [loading, setLoading] = useState(true);

  const fetchPatient = async () => {
    try {
      const patientResponse = await getPatientById(props.patientId);
      if (patientResponse?.ok) {
        const body = await patientResponse.json();
        setPatient(body);
      }
    } catch (error) {
      rollbar.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientBalance = async () => {
    try {
      const balanceResponse = await patientData.getBalance(props.patientId);
      if (balanceResponse) {
        const remainingPatientAmount =
          balanceResponse?.remainingPatientAmount || 0;
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        });
        setBalance(formatter.format(remainingPatientAmount));
      }
    } catch (error) {
      rollbar.error(error);
    }
  };

  const displayName = useMemo(() => {
    if (patient) {
      if (isPreferredNameFTEnabled && patient.preferredName) {
        return `${patient.preferredName} ${patient.lastName}`;
      } else {
        return `${patient.firstName} ${patient.lastName}`;
      }
    }
    return '';
  }, [patient]);

  const legalName = useMemo(() => {
    if (patient && isPreferredNameFTEnabled) {
      return formatPatientName(patient);
    }
    return '';
  }, [patient, isPreferredNameFTEnabled]);

  const patientDoBString = useMemo(() => {
    if (patient) {
      return `${formatISODateShort(patient.dob)} (${patient.ageInYears} yo)`;
    }
    return '';
  }, [patient]);

  useEffect(() => {
    if (props.patientId > 0) {
      fetchPatient();
      fetchPatientBalance();
      featureToggles.isEnabled(PREFERRED_NAME_PRONOUNS_FT).then(enabled => {
        setIsPreferredNameFTEnabled(enabled);
      });
    }
  }, [props.patientId]);

  return (
    <ThemeProvider theme={dsTheme}>
      <React.Fragment>
        {loading ? (
          <Box display="flex" flexDirection="column">
            <Skeleton variant="text" height={40} />
            <Skeleton variant="text" height={40} />
            <Skeleton variant="rectangular" width="100%" height={100} />
          </Box>
        ) : (
          <>
            <Box mb="8px" lineHeight="18px">
              <H3 style={{ wordWrap: 'break-word' }}>{displayName}</H3>
              <If
                condition={
                  props.userChartAccess?.clinicalDataAccessRestrictionEnabled ||
                  props.userChartAccess?.chartAccessRestrictionEnabled
                }
              >
                <AccessLink
                  linkLabel={
                    props.userChartAccess.patientRestricted
                      ? 'Restricted'
                      : 'Unrestricted'
                  }
                  patientId={props.patientId}
                  firstName={props.firstName}
                  lastName={props.lastName}
                  protectionSettings={props.protectionSettings}
                  userChartAccess={props.userChartAccess}
                  togglePatientRestriction={props.togglePatientRestriction}
                  style={{ color: colors.primary }}
                />
              </If>
            </Box>
            <Row>
              <Col xs={8} style={{ paddingRight: '.25rem' }}>
                <img
                  style={{
                    height: '105px',
                    marginBottom: 0,
                    boxShadow: '0 1px 2px rgb(0 0 0 / 10%)',
                  }}
                  src={patientPhotoUrl}
                  onError={e => {
                    e.target.onerror = null;
                    e.target.src = 'img/empty-profile-m.png';
                  }}
                />
              </Col>
              <Col
                className="patient-summary"
                xs={4}
                style={{ paddingLeft: 0 }}
              >
                <Card
                  className="patient-controls card wire"
                  style={{
                    height: '105px',
                    marginBottom: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip
                    position="right-start"
                    theme="light"
                    arrow
                    interactive
                    html={
                      <>
                        <Typography component="div" align="left">
                          Patient Details
                        </Typography>
                        <DetailsTable
                          style={{ minWidth: '15rem', margin: '.5rem 0' }}
                        >
                          <tbody>
                            {isPreferredNameFTEnabled &&
                              patient?.preferredName && (
                                <tr>
                                  <th style={{ width: '30%' }}>Legal Name</th>
                                  <td>{legalName}</td>
                                </tr>
                              )}
                            <tr>
                              <th>Sex</th>
                              <td>{patient?.gender || ''}</td>
                            </tr>
                            {isPreferredNameFTEnabled && patient?.pronouns && (
                              <tr>
                                <th>Pronouns</th>
                                <td>{patient.pronouns}</td>
                              </tr>
                            )}
                            <tr>
                              <th>DoB</th>
                              <td>{patientDoBString}</td>
                            </tr>
                            <tr>
                              <th>Balance</th>
                              <td>{balance}</td>
                            </tr>
                          </tbody>
                        </DetailsTable>
                      </>
                    }
                  >
                    <Box pl="2px">
                      <AttentionFilledIcon color="primary" fontSize="small" />
                    </Box>
                  </Tooltip>
                  <Tooltip
                    position="right-start"
                    theme="light"
                    arrow
                    html={
                      <Banner
                        style={{ width: '13rem', textAlign: 'left' }}
                        type="info"
                        message={COMING_SOON_MESSAGE}
                      />
                    }
                  >
                    <PlusOutlinedIcon color="primary" fontSize="small" />
                  </Tooltip>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </React.Fragment>
    </ThemeProvider>
  );
};

PatientInfoWidget.propTypes = {
  patientId: PropTypes.number,
  protectionSettings: PropTypes.object,
  userChartAccess: PropTypes.object,
  togglePatientRestriction: PropTypes.func,
};

PatientInfoWidget.defaultProps = {
  patientId: 0,
  protectionSettings: {},
  userChartAccess: {},
  togglePatientRestriction: () => {},
};

export default PatientInfoWidget;
